/**
 * Turn `series[]` into Plotly traces over a common x. `makeSeriesTraces` applies
 * one geometry to every series (line, bar, distribution); `makeLineBarTraces`
 * resolves the geometry per series from `series[].type`, so bars and lines mix
 * in one chart. Both delegate to `buildSeriesTrace`. Extracted from the old
 * ChartRenderer; colors come from the injected palette, not a private constant.
 */

import type { DataPoint, DataSeries } from '../../types/dashboard';
import type { ChartTrace } from '../types';
import { colorAt, darken } from '../theme/palette';
import { DEFAULT_MARKER_SIZE, SERIES_LINE_WIDTH } from '../theme/layout';
import { toFiniteNumber } from './number';
import { monotoneCurve } from './monotone';

export type SeriesKind = 'line' | 'bar' | 'distribution';

/**
 * Build a Plotly `error_y` object from a column of symmetric (±value) error
 * magnitudes. Returns undefined when no `errorKey` is given, so callers can
 * spread it conditionally. Asymmetric (high/low) errors are out of scope here.
 *
 * A blank/non-numeric error cell maps to `null` (no bar for that point) rather
 * than `Number('')→0`, which would draw a false zero-length bar. `null` keeps
 * the array aligned with the plotted points.
 */
export function makeErrorY(
  data: DataPoint[],
  errorKey?: string
): { error_y: Record<string, unknown> } | undefined {
  if (!errorKey) return undefined;
  return {
    error_y: {
      type: 'data',
      array: data.map((d) => toFiniteNumber(d[errorKey])),
      visible: true,
    },
  };
}

/**
 * Build one Plotly trace for a single series with an explicit geometry `kind`.
 * Shared by the uniform-geometry charts (line/bar/distribution, via
 * `makeSeriesTraces`) and the mixed-geometry line+bar (via `makeLineBarTraces`),
 * which resolves the kind per series. The right axis, error bars and dash are
 * applied here so both callers behave identically per trace.
 */
function buildSeriesTrace(
  data: DataPoint[],
  x: (string | number | null)[],
  s: DataSeries,
  color: string,
  kind: SeriesKind,
  showDataLabels: boolean,
  scale: number,
  markerSize: number
): ChartTrace {
  const base: ChartTrace = {
    x,
    y: data.map((d) => d[s.dataKey]),
    name: s.name,
    ...makeErrorY(data, s.errorKey),
    // Opt-in second axis: undefined/'left' keeps Plotly's default 'y'.
    ...(s.yAxis === 'right' ? { yaxis: 'y2' } : {}),
  };
  if (kind === 'bar') {
    const bar: ChartTrace = { ...base, type: 'bar', marker: { color } };
    // Value labels over each bar (opt-in). Thousands-separated, colored a
    // darkened series tone for readability (keeps association, gains contrast),
    // and cliponaxis:false so the topmost label is never clipped. bar ignores `dash`.
    if (showDataLabels) {
      bar.texttemplate = '%{y:,}';
      bar.textposition = 'outside';
      bar.textfont = { color: darken(color) };
      bar.cliponaxis = false;
    }
    return bar;
  }
  if (kind === 'distribution') {
    return {
      ...base,
      type: 'scatter',
      mode: s.markers ? 'lines+markers' : 'lines',
      fill: 'tozeroy',
      line: { color, dash: s.dash ?? 'solid', shape: s.smooth ? 'spline' : 'linear' },
      ...(s.markers ? { marker: { color, size: markerSize * scale } } : {}),
    };
  }
  return {
    ...base,
    type: 'scatter',
    mode: s.markers ? 'lines+markers' : 'lines',
    line: {
      color,
      width: SERIES_LINE_WIDTH * scale,
      dash: s.dash ?? 'solid',
      shape: s.smooth ? 'spline' : 'linear',
    },
    ...(s.markers ? { marker: { color, size: markerSize * scale } } : {}),
  };
}

export function makeSeriesTraces(
  data: DataPoint[],
  x: (string | number | null)[],
  series: DataSeries[],
  palette: readonly string[],
  kind: SeriesKind,
  showDataLabels = false,
  // Stroke width scales with the figure (see ../theme/scale); 1 keeps the
  // authored width for callers that have no measurement to scale against.
  scale = 1,
  // Caller passes `model.options?.markerSize`; undefined falls back here.
  markerSize: number = DEFAULT_MARKER_SIZE,
  // `model.options?.smoothMonotone` — see ChartOptions. Only the 'line' kind reads it.
  smoothMonotone = false
): ChartTrace[] {
  return series.flatMap((s, i) => {
    const color = s.color || colorAt(i, palette);
    if (kind === 'line' && s.smooth && smoothMonotone) {
      return monotoneSeriesTraces(data, x, s, color, scale, markerSize);
    }
    return [buildSeriesTrace(data, x, s, color, kind, showDataLabels, scale, markerSize)];
  });
}

/**
 * A smoothed line series drawn without overshooting its data (Bug 2764): the curve
 * is the sampled monotone interpolation as a `linear`, hover-less trace, and the
 * real points ride on a second trace that carries the hover, markers and error
 * bars. Both share one legend group, with the legend entry on the curve. An X that
 * cannot be interpolated falls back to straight segments, never to Plotly's spline.
 */
function monotoneSeriesTraces(
  data: DataPoint[],
  x: (string | number | null)[],
  s: DataSeries,
  color: string,
  scale: number,
  markerSize: number
): ChartTrace[] {
  const straight = buildSeriesTrace(
    data,
    x,
    { ...s, smooth: false },
    color,
    'line',
    false,
    scale,
    markerSize
  );
  const curve = monotoneCurve(x, straight.y as unknown[]);
  if (!curve) return [straight];
  const { line, marker, ...points } = straight;
  return [
    {
      type: 'scatter',
      mode: 'lines',
      x: curve.x,
      y: curve.y,
      name: s.name,
      line,
      hoverinfo: 'skip',
      legendgroup: s.dataKey,
      ...(s.yAxis === 'right' ? { yaxis: 'y2' } : {}),
    },
    {
      ...points,
      mode: 'markers',
      marker: marker ?? { color, size: markerSize * scale, opacity: 0 },
      showlegend: false,
      legendgroup: s.dataKey,
    },
  ];
}

/** Map a series' declared `type` to a geometry; default (unset) is a line. */
function lineBarKind(s: DataSeries): SeriesKind {
  if (s.type === 'bar') return 'bar';
  if (s.type === 'area') return 'distribution';
  return 'line';
}

/**
 * Like `makeSeriesTraces`, but the geometry is resolved **per series** from
 * `series[].type` instead of a single chart-wide kind — bars and lines coexist
 * in one chart (e.g. metal bars + grade line on a second axis). The palette
 * index runs over the whole series list so colors stay stable regardless of
 * each series' geometry.
 */
export function makeLineBarTraces(
  data: DataPoint[],
  x: (string | number | null)[],
  series: DataSeries[],
  palette: readonly string[],
  showDataLabels = false,
  scale = 1,
  markerSize: number = DEFAULT_MARKER_SIZE
): ChartTrace[] {
  return series.map((s, i) =>
    buildSeriesTrace(
      data,
      x,
      s,
      s.color || colorAt(i, palette),
      lineBarKind(s),
      showDataLabels,
      scale,
      markerSize
    )
  );
}
